// The six editable flags, in column order, with their bits as the API sends them (UserLoginPermissionTypes).
// `role` is the role filter value that selects the flag, `header` the column's translation key.
export const PERMISSION_FLAGS = [
  { flag: 'canCreateUsers', bit: 1, role: 'createUsers', header: 'admin.userLoginList.table.createUsers' },
  { flag: 'canCreateUserGroups', bit: 2, role: 'createGroups', header: 'admin.userLoginList.table.createUserGroups' },
  { flag: 'canDoMetrology', bit: 4, role: 'metrology', header: 'admin.userLoginList.table.canDoMetrology' },
  { flag: 'isExpert', bit: 512, role: 'expert', header: 'admin.userLoginList.table.isExpert' },
  { flag: 'isOperator', bit: 1024, role: 'operator', header: 'admin.userLoginList.table.isOperator' },
  { flag: 'isAdmin', bit: 8192, role: 'admin', header: 'admin.userLoginList.table.isAdmin' },
];

const ALL_FLAG_BITS = PERMISSION_FLAGS.reduce((all, f) => all | f.bit, 0);

// The role select in display order: `all`, a role per flag, then `none`.
export const ROLE_FILTERS = ['all', 'admin', 'expert', 'operator', 'metrology', 'createUsers', 'createGroups', 'none'];

export function hasBit(permissions, bit) {
  return (permissions & bit) !== 0;
}

export function withBit(permissions, bit, on) {
  return on ? permissions | bit : permissions & ~bit;
}

// First and last name, whichever are present; empty when neither is.
function fullNameOf(row) {
  return `${row.firstName || ''} ${row.lastName || ''}`.trim();
}

// First and last name, else the display name; also the Name column's sort key.
export function nameOf(row) {
  return fullNameOf(row) || row.displayName || '';
}

// Filters on the loaded permissions, not pending edits, so ticking a box never hides the row being edited.
export function matchesFilters(row, text, role) {
  const needle = text.trim().toLowerCase();
  if (needle) {
    const haystacks = [fullNameOf(row), row.displayName, row.email];
    if (!haystacks.some(h => (h || '').toLowerCase().includes(needle))) return false;
  }

  if (role === 'all') return true;
  if (role === 'none') return (row.permissions & ALL_FLAG_BITS) === 0;
  const entry = PERMISSION_FLAGS.find(f => f.role === role);
  if (!entry) throw new Error(`Unknown role filter: ${role}`);
  return hasBit(row.permissions, entry.bit);
}

// A pending edit is the login's whole new permissions value, keyed by uid.
export function permissionsOf(row, edits) {
  return edits.get(row.uid) ?? row.permissions;
}

// What Save sends: an edit that ends where it started is not a change.
export function changedLogins(rows, edits) {
  return rows
    .filter(row => edits.has(row.uid) && edits.get(row.uid) !== row.permissions)
    .map(row => ({ uid: row.uid, permissions: edits.get(row.uid) }));
}

// After a save: the saved logins take their new permissions, every other row stays the same object.
export function applySaved(rows, changed) {
  const saved = new Map(changed.map(x => [x.uid, x.permissions]));
  return rows.map(row => (saved.has(row.uid) ? { ...row, permissions: saved.get(row.uid) } : row));
}

// A users fetch lands only over its own pending entry. Refresh stays enabled while it runs and empties the
// cache, so a late pre-Refresh result must not refill it; a collapsed row still gets its result.
export function settleUsersEntry(cache, uid, pending, next) {
  return cache.get(uid) === pending ? new Map(cache).set(uid, next) : cache;
}
